<template>
  <div
    v-if="isOpen"
    data-testid="global-search-overlay"
    class="fixed inset-0 z-50 overflow-y-auto"
    role="dialog"
    aria-modal="true"
    aria-label="Search"
    @click="handleOverlayClick"
  >
    <!-- Backdrop -->
    <div
      class="fixed inset-0 bg-primary-900/40 backdrop-blur-sm transition-opacity animate-fade"
    />

    <!-- Search Panel -->
    <div class="relative min-h-screen flex items-start justify-center p-4 pt-16 sm:pt-24">
      <div
        ref="searchPanelRef"
        class="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl transform transition-all border border-gray-200 animate-in overflow-hidden"
      >
        <!-- Search Input -->
        <div class="flex items-center px-5 py-4 border-b border-gray-100">
          <svg
            class="w-5 h-5 text-gray-400 mr-3 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            ref="searchInputRef"
            id="global-search-input"
            v-model="query"
            type="text"
            placeholder="Search properties, tenants, leases..."
            aria-label="Search properties, tenants, leases"
            class="flex-1 text-base outline-none placeholder-gray-400 bg-transparent"
            @keydown="handleKeydown"
          />
          <kbd class="hidden sm:inline-flex items-center px-2 py-1 text-xs text-gray-400 bg-gray-100 rounded-md font-mono mr-2">esc</kbd>
          <button
            aria-label="Close search"
            class="min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            @click="emit('close')"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="p-8 text-center">
          <div class="spinner mx-auto" />
          <p class="mt-3 text-sm text-gray-500">Searching...</p>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="p-8 text-center text-red-500 text-sm">
          <p>{{ error }}</p>
        </div>

        <!-- Results -->
        <div v-else-if="hasResults" data-testid="global-search-results" class="max-h-96 overflow-y-auto">
          <div
            v-for="(result, index) in flatResults"
            :key="result.id"
            data-testid="global-search-result"
            :data-selected="index === selectedIndex ? 'true' : undefined"
            :class="[
              'flex items-center px-5 py-3 cursor-pointer transition-all duration-150',
              index === selectedIndex ? 'bg-blue-50' : 'hover:bg-gray-50',
            ]"
            @click="selectResult(result)"
          >
            <div
              class="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center mr-3"
              :class="getTypeIconClasses(result.type)"
            >
              <svg
                v-if="getSearchIconType(result.type) === 'property'"
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <svg
                v-else-if="getSearchIconType(result.type) === 'unit'"
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
              <svg
                v-else-if="getSearchIconType(result.type) === 'tenant'"
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <svg
                v-else
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-gray-900 truncate">{{ result.title }}</p>
              <p v-if="result.subtitle" class="text-xs text-gray-500 truncate">{{ result.subtitle }}</p>
            </div>
            <span
              class="ml-2 px-2 py-0.5 text-[10px] font-semibold rounded-full uppercase tracking-wider"
              :class="getTypeBadgeClasses(result.type)"
            >
              {{ getTypeLabel(result.type) }}
            </span>
          </div>
        </div>

        <!-- No Results -->
        <div v-else-if="query.length >= 2 && !loading" class="py-12 px-8 text-center">
          <div class="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <p class="text-sm text-gray-500">
            No results for "<span class="font-semibold text-gray-700">{{ query }}</span>"
          </p>
        </div>

        <!-- Initial State -->
        <div v-else-if="query.length < 2" class="py-10 px-8 text-center">
          <p class="text-sm text-gray-400">Type at least 2 characters to search</p>
        </div>

        <!-- Keyboard Hints -->
        <div
          v-if="hasResults"
          class="px-5 py-2.5 border-t border-gray-100 text-[11px] text-gray-400 flex items-center justify-end space-x-4 bg-gray-50/50"
        >
          <span><kbd class="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono">↑</kbd> <kbd class="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono">↓</kbd> navigate</span>
          <span><kbd class="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono">↵</kbd> select</span>
          <span><kbd class="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono">esc</kbd> close</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { api } from '@/api';
import { validateSearchResultsShape } from '@/api-validators';
import { useFocusTrap } from '@/composables/useFocusTrap';

const router = useRouter();

interface SearchResult {
  id: string;
  type: string;
  title: string;
  subtitle?: string;
}

interface SearchResults {
  results?: SearchResult[];
}

interface Props {
  isOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
});

const emit = defineEmits<{
  close: [];
}>();

const query = ref('');
const results = ref<SearchResults | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const selectedIndex = ref(-1);
const searchInputRef = ref<HTMLInputElement | null>(null);
const searchPanelRef = ref<HTMLElement | null>(null);
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let searchAbortController: AbortController | null = null;
const { activate, deactivate } = useFocusTrap();

const flatResults = computed(() => {
  if (!results.value) return [];
  return results.value.results ?? [];
});

const hasResults = computed(() => flatResults.value.length > 0);

const VALID_TYPES = ['property', 'unit', 'tenant', 'lease'] as const;

function reset(): void {
  query.value = '';
  results.value = null;
  loading.value = false;
  error.value = null;
  selectedIndex.value = -1;
  if (searchAbortController) {
    searchAbortController.abort();
    searchAbortController = null;
  }
}

function debouncedSearch(): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => search(), 300);
}

async function search(): Promise<void> {
  if (query.value.length < 2) return;

  // Abort any in-flight search request to prevent stale results
  if (searchAbortController) {
    searchAbortController.abort();
  }
  searchAbortController = new AbortController();
  const { signal } = searchAbortController;

  loading.value = true;
  error.value = null;

  try {
    const data = await api.validatedGet<SearchResults>(
      `/api/search?q=${encodeURIComponent(query.value)}&limit=20`,
      validateSearchResultsShape,
      { signal },
    );
    if (signal.aborted) return;
    // Don't update if this request was aborted (a newer search is in flight)
    if (!signal.aborted) {
      results.value = data ?? null;
    }
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') return; // Intentional abort
    error.value = err instanceof Error ? err.message : 'Search failed';
  } finally {
    if (!signal.aborted) {
      loading.value = false;
    }
  }
}

function selectResult(result: SearchResult): void {
  router.push({ name: getRouteForResult(result), params: { id: result.id } });
  emit('close');
}

function getRouteForResult(result: SearchResult): string {
  const routes: Record<string, string> = {
    property: 'property-detail',
    unit: 'unit-detail',
    tenant: 'tenant-detail',
    lease: 'lease-detail',
  };
  return routes[result.type] ?? 'dashboard';
}

function getSearchIconType(type: string): string {
  return VALID_TYPES.includes(type as (typeof VALID_TYPES)[number]) ? type : 'property';
}

function getTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    property: 'Property',
    unit: 'Unit',
    tenant: 'Tenant',
    lease: 'Lease',
  };
  return labels[type] ?? type;
}

function getTypeBadgeClasses(type: string): string {
  const classes: Record<string, string> = {
    property: 'bg-blue-50 text-blue-700',
    unit: 'bg-purple-50 text-purple-700',
    tenant: 'bg-green-50 text-green-700',
    lease: 'bg-amber-50 text-amber-700',
  };
  return classes[type] ?? 'bg-gray-50 text-gray-700';
}

function getTypeIconClasses(type: string): string {
  const classes: Record<string, string> = {
    property: 'bg-blue-50 text-blue-600',
    unit: 'bg-purple-50 text-purple-600',
    tenant: 'bg-green-50 text-green-600',
    lease: 'bg-amber-50 text-amber-600',
  };
  return classes[type] ?? 'bg-gray-50 text-gray-600';
}

function handleKeydown(e: KeyboardEvent): void {
  if (!hasResults.value) {
    if (e.key === 'Escape') emit('close');
    return;
  }

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    selectedIndex.value = Math.min(selectedIndex.value + 1, flatResults.value.length - 1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    selectedIndex.value = Math.max(selectedIndex.value - 1, -1);
  } else if (e.key === 'Enter' && selectedIndex.value >= 0) {
    e.preventDefault();
    selectResult(flatResults.value[selectedIndex.value]);
  } else if (e.key === 'Escape') {
    emit('close');
  }
}

function handleOverlayClick(e: MouseEvent): void {
  if (e.target === e.currentTarget) {
    emit('close');
  }
}

watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        activate(searchPanelRef.value);
        searchInputRef.value?.focus();
      });
    } else {
      deactivate();
      reset();
    }
  }
);

onBeforeUnmount(() => {
  deactivate();
});

watch(query, (newVal) => {
  selectedIndex.value = -1;
  if (newVal.length >= 2) {
    debouncedSearch();
  } else {
    results.value = null;
  }
});
</script>
